import { AxiosBatchData } from './AxiosBatchData'
import {
  Species,
  SpeciesBinomen,
  SpeciesFootNumber,
  SpeciesVolume,
  SpeciesUnitCost,
  SpeciesObservations,
  SpeciesTarget
} from '@/Domain/Batch/Species'
import {
  BatchFactory,
  BatchFactoryError,
  BatchId,
  Batch,
  BatchType,
  BatchCertifications,
  BatchCertificationFlag,
  BatchSituation,
  BatchPurpose,
  BatchIntervention,
  BatchLocation,
  BatchMount,
  BatchOwner,
  BatchCounty,
  BatchSurface,
  BatchEconomicInfo,
  BatchWindow,
  BatchAlreadyWithOfferFlag
} from '@/Domain/Batch'
import { Currency } from '@/Domain/Batch/Currency'
import { AxiosSpeciesData } from './AxiosSpeciesData'

export class AxiosBatchFactory implements BatchFactory<AxiosBatchData> {
  makeBatch (data: AxiosBatchData): Batch {
    try {
      const species = this.parseSpecies(data.species)
      const batch = new Batch(
        new BatchId(data.batchId, data.recordId),
        data.batchType as BatchType,
        this.parseCertifications(data),
        this.parseLocation(data),
        this.parseIntervention(data),
        new BatchSurface(data.surface),
        this.parseEconomicInfo(data),
        new BatchWindow(data.window),
        this.parseVolumeUnit(data.volumeUnit),
        this.parseAlreadyWithOfferFlag(data.alreadyWithOffer),
        species
      )
      return batch
    } catch (e) {
      throw new BatchFactoryError(e.message)
    }
  }

  private parseVolumeUnit (volumeUnit: string): string {
    if (volumeUnit === 'M3') {
      return 'm3'
    }
    if (volumeUnit === 'TN') {
      return 't'
    }
    return volumeUnit
  }

  private parseLocation (data: AxiosBatchData): BatchLocation {
    const county = data.county ? new BatchCounty(data.county) : null
    return new BatchLocation(
      new BatchMount(data.mount),
      new BatchOwner(data.owner),
      county
    )
  }

  private parseCertifications (data: AxiosBatchData): BatchCertifications {
    return new BatchCertifications(
      this.parseCertificationFlag(data.uneCertification),
      this.parseCertificationFlag(data.fscCertification)
    )
  }

  private parseCertificationFlag (value: boolean): BatchCertificationFlag {
    return value ? BatchCertificationFlag.isTrue() : BatchCertificationFlag.isFalse()
  }

  private parseAlreadyWithOfferFlag (value: boolean): BatchAlreadyWithOfferFlag {
    return value ? BatchAlreadyWithOfferFlag.isTrue() : BatchAlreadyWithOfferFlag.isFalse()
  }

  private parseIntervention (data: AxiosBatchData): BatchIntervention {
    return new BatchIntervention(
      data.purposeCode as BatchPurpose,
      data.situationCode as BatchSituation
    )
  }

  private parseEconomicInfo (data: AxiosBatchData): BatchEconomicInfo {
    const temporaryDeposit = data.temporaryDeposit
      ? new Currency(data.temporaryDeposit)
      : undefined
    return new BatchEconomicInfo(
      new Currency(data.appraisal),
      new Currency(data.taxes),
      temporaryDeposit
    )
  }

  private parseSpecies (data: AxiosSpeciesData[]): Species[] {
    const species: Species[] = []
    for (const item of data) {
      species.push(this.parseSingleSpecies(item))
    }
    return species
  }

  private parseSingleSpecies (data: AxiosSpeciesData): Species {
    const footNumber = data.footNumber ? new SpeciesFootNumber(data.footNumber) : null
    const observations = data.observations ? new SpeciesObservations(data.observations) : null
    const speciesCost = data.cost !== undefined ? new SpeciesUnitCost(data.cost) : null
    const species = new Species(
      data.internalId,
      new SpeciesBinomen(data.binomen),
      footNumber,
      new SpeciesVolume(data.volume),
      data.morphologyCode,
      speciesCost,
      this.parseSpeciesTarget(data, 'sawOne'),
      this.parseSpeciesTarget(data, 'sawTwo'),
      this.parseSpeciesTarget(data, 'grinded'),
      observations
    )
    return species
  }

  private parseSpeciesTarget (data: AxiosSpeciesData, target: string): SpeciesTarget | null {
    const volumeKey = target + 'Volume' as keyof AxiosSpeciesData
    const costKey = target + 'Cost' as keyof AxiosSpeciesData
    if (!data[volumeKey]) {
      return null
    }
    const volume = data[volumeKey] as number
    const cost = data[costKey] as number
    return new SpeciesTarget(
      new SpeciesUnitCost(cost),
      new SpeciesVolume(volume)
    )
  }
}
